/**
 * The app bar's count pills ("Today 6 · Verify 14 · Release 190" in the mock), without a shared
 * file to edit: a page publishes its own count with `usePublishNavCount('/verify', n)`, and the
 * app bar reads it with `useNavCount('/verify')`. A count is kept after its page unmounts, so
 * the pill stays while the person is elsewhere. `undefined` (never published) shows no pill.
 */

import { useEffect, useSyncExternalStore } from 'react';

const counts = new Map<string, number>();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setNavCount(path: string, count: number | undefined): void {
  const prev = counts.get(path);
  if (count === undefined) {
    if (!counts.has(path)) return;
    counts.delete(path);
  } else {
    if (prev === count) return;
    counts.set(path, count);
  }
  listeners.forEach((l) => l());
}

export function useNavCount(path: string): number | undefined {
  return useSyncExternalStore(
    subscribe,
    () => counts.get(path),
    () => counts.get(path),
  );
}

/** Pages call this with their current count (`undefined` while loading keeps the last one). */
export function usePublishNavCount(path: string, count: number | undefined): void {
  useEffect(() => {
    if (count !== undefined) setNavCount(path, count);
  }, [path, count]);
}
